import jsPDF from 'jspdf';
import { formatPrice } from '../../../../rbts/helpers';

const loadImageAsBase64 = async (url: string): Promise<string | null> => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

/**
 * Genera y descarga un único PDF con secciones por Tipo de punto de venta.
 * Cada conciliación ocupa su propia página (o páginas si tiene muchos productos).
 * Las conciliaciones se agrupan y ordenan por tipo.
 */
export const generarPdfMasivo = async (conciliaciones: any[], strDate: string): Promise<void> => {
  if (!conciliaciones || conciliaciones.length === 0) return;

  const doc = new jsPDF({ format: 'letter' });
  const logoBase64 = await loadImageAsBase64(process.env.PUBLIC_URL + '/media/logos/logo_2.png');

  // Agrupar por tipo de punto de venta
  const grupos: Record<string, any[]> = {};
  for (const c of conciliaciones) {
    const tipo = c.punto_venta?.tipo || 'Otros';
    if (!grupos[tipo]) grupos[tipo] = [];
    grupos[tipo].push(c);
  }

  const itemsPerPage = 17;
  let isFirstPage = true;

  for (const [tipo, lista] of Object.entries(grupos)) {
    for (const conciliacion of lista) {
      if (!isFirstPage) {
        doc.addPage();
      }
      isFirstPage = false;

      // ── Encabezado de página ──
      if (logoBase64) {
        doc.addImage(logoBase64, 'PNG', 150, 10, 40, 25);
      }
      doc.setFontSize(14);
      doc.setTextColor('#000');
      doc.text('Conciliación Masiva con proveedores', 20, 25);

      doc.setFontSize(10);
      doc.setTextColor('#000');
      doc.text(`Fecha administrativa: ${strDate}`, 20, 35);

      // ── Sección por Tipo ──
      doc.setFontSize(11);
      doc.setTextColor('#000');
      doc.text(`Tipo: ${tipo}`, 20, 48);
      doc.setDrawColor('#000');
      doc.line(20, 51, 195, 51);

      // ── Info del puesto ──
      doc.setFontSize(10);
      doc.setTextColor('#000');
      doc.text(`Nombre del puesto: ${conciliacion.punto_venta?.nombre || ''}`, 20, 60);
      doc.text(`Folio: ${conciliacion.folio || ''}`, 20, 68);

      // ── Encabezado de tabla ──
      let y = 80;
      doc.setFontSize(9);
      doc.setTextColor('#000');
      doc.text('Productos',    14, y);
      doc.text('Precio',       62, y);
      doc.text('Cantidad',     86, y);
      doc.text('Saldo elect.', 106, y);
      doc.text('Banco',        140, y);
      doc.text('Proveedor',    158, y);
      doc.text('M. prima',     177, y);
      doc.text('Total',        198, y);
      doc.setTextColor('#000');
      y += 9;

      // ── Filas de productos ──
      const detalle: any[] = conciliacion.detalle || [];
      let itemCount = 0;

      for (const item of detalle) {
        // Nueva página si se agotó el espacio
        if (itemCount > 0 && itemCount % itemsPerPage === 0) {
          doc.addPage();
          y = 20;
        }

        doc.setFontSize(9);
        doc.setTextColor('#000');
        const maxChars = item.es_cortesia ? 14 : 22;
        const nombreProducto =
          item.producto_nombre && item.producto_nombre.length > maxChars
            ? item.producto_nombre.substring(0, maxChars) + '...'
            : item.producto_nombre || '';

        doc.text(nombreProducto, 14, y);
        if (item.es_cortesia) {
          const nameWidth = doc.getTextWidth(nombreProducto);
          doc.setFontSize(8);
          doc.setTextColor('#4A5DDC');
          doc.text('Cortesía', 14 + nameWidth + 2, y);
          doc.setFontSize(9);
          doc.setTextColor('#000');
        }
        doc.text(formatPrice(item.precio || 0, 'es-MX', 'MXN', 2, 2).toString(), 62, y);
        doc.text(item.cantidad.toString(), 86, y);
        doc.text(formatPrice(item.saldo_electronico || 0, 'es-MX', 'MXN', 2, 2).toString(), 106, y);
        doc.text(formatPrice(item.monto_banco || 0, 'es-MX', 'MXN', 2, 2).toString(), 140, y);
        doc.text(formatPrice(item.monto_punto_venta || 0, 'es-MX', 'MXN', 2, 2).toString(), 158, y);

        y += 9;
        itemCount++;
      }

      // ── Fila de totales ──
      y += 3;
      doc.setFontSize(9);
      doc.setTextColor('#000');
      doc.text('Total', 62, y);
      doc.setTextColor('#000');
      doc.text((conciliacion.total_cantidad || 0).toString(), 86, y);
      doc.text(formatPrice(conciliacion.total_saldo_electronico || 0, 'es-MX', 'MXN', 2, 2).toString(), 106, y);
      doc.text(formatPrice(conciliacion.total_banco || 0, 'es-MX', 'MXN', 2, 2).toString(), 140, y);
      doc.text(formatPrice(conciliacion.total_punto_venta || 0, 'es-MX', 'MXN', 2, 2).toString(), 158, y);
      doc.text(formatPrice(conciliacion.total_materia_prima || 0, 'es-MX', 'MXN', 2, 2).toString(), 177, y);
      doc.text(formatPrice(conciliacion.total_a_pagar || 0, 'es-MX', 'MXN', 2, 2).toString(), 198, y);

      // ── Firmas ──
      const userName = conciliacion.user
        ? `${conciliacion.user.first_name} ${conciliacion.user.father_last_name}`
        : '';
      doc.text(conciliacion.punto_venta?.nombre || '', 33, 260);
      doc.text(userName, 163, 260);
    }
  }

  const today = new Date().toISOString().split('T')[0];
  doc.save(`conciliacion_masiva_${today}.pdf`);
};
